import { isDefined } from "@clipboard-health/util-ts";
import { minutesToMilliseconds } from "date-fns";
import { useMemo } from "react";

import { useGetMultipleChoiceAggregates } from "../api/useGetMultipleChoiceAggregates";
import { QuestionTag } from "../types";

const SUB_CATEGORY_TAGS = [
  QuestionTag.SUPPLY_RATING,
  QuestionTag.CLEANLINESS,
  QuestionTag.PATIENT_CARE,
  QuestionTag.CULTURE,
] as const;

type SubCategoryTag = (typeof SUB_CATEGORY_TAGS)[number];

const SUB_CATEGORY_LABEL: Record<SubCategoryTag, string> = {
  [QuestionTag.SUPPLY_RATING]: "Supplies",
  [QuestionTag.CLEANLINESS]: "Cleanliness",
  [QuestionTag.PATIENT_CARE]: "Patient care",
  [QuestionTag.CULTURE]: "Friendliness",
};

const SUB_CATEGORY_TAG_SET = new Set<string>(SUB_CATEGORY_TAGS);

export interface SubCategoryRating {
  tag: QuestionTag;
  label: string;
  averageRating: number;
  ratingsCount: number;
}

interface UseSubCategoryRatingsResult {
  subCategoryRatings: SubCategoryRating[];
  isLoading: boolean;
}

interface UseSubCategoryRatingsProps {
  workplaceId: string;
}

// eslint-disable-next-line security/detect-unsafe-regex -- pattern is safe, input is bounded API text
const RATING_PATTERN = /rating is (\d+(?:\.\d+)?) out of 5/;

export function parseRatingFromText(text: string): number | undefined {
  const match = RATING_PATTERN.exec(text);
  return match ? Number.parseFloat(match[1]) : undefined;
}

export function useSubCategoryRatings(
  props: UseSubCategoryRatingsProps
): UseSubCategoryRatingsResult {
  const { workplaceId } = props;

  const { data: multipleChoiceAggregates = [], isLoading } = useGetMultipleChoiceAggregates(
    workplaceId,
    {
      enabled: isDefined(workplaceId) && workplaceId !== "",
      staleTime: minutesToMilliseconds(5),
      useErrorBoundary: false,
    }
  );

  const subCategoryRatings = useMemo(() => {
    // Group aggregates by tag, keeping the one with the highest repliesCount
    const bestByTag = new Map<string, { tag: QuestionTag; rating: number; repliesCount: number }>();

    for (const aggregate of multipleChoiceAggregates) {
      const matchingTag = aggregate.attributes.question.tags.find((t) =>
        SUB_CATEGORY_TAG_SET.has(t.tag)
      );

      if (!matchingTag) {
        continue;
      }

      const rating = parseRatingFromText(aggregate.attributes.text);
      if (rating === undefined) {
        continue;
      }

      const existing = bestByTag.get(matchingTag.tag);
      if (!existing || aggregate.attributes.repliesCount > existing.repliesCount) {
        bestByTag.set(matchingTag.tag, {
          tag: matchingTag.tag,
          rating,
          repliesCount: aggregate.attributes.repliesCount,
        });
      }
    }

    // Return in defined display order, including categories with no data
    return SUB_CATEGORY_TAGS.map((tag) => {
      const entry = bestByTag.get(tag);
      return {
        tag,
        label: SUB_CATEGORY_LABEL[tag] ?? tag,
        averageRating: entry?.rating ?? 0,
        ratingsCount: entry?.repliesCount ?? 0,
      };
    });
  }, [multipleChoiceAggregates]);

  return {
    subCategoryRatings,
    isLoading,
  };
}
